import { matchRoutes, useLocation, useNavigate } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { useAppSelector } from '@store/hooks';
import { selectUsers } from '@store/slices/users/usersSlice';
import { LoaderComponent } from '../LoaderComponent/LoaderComponent';
import { shouldSkipAuth } from '@utils/auth';
import { resolveRole } from '@routes/resolveRole';
import { routesByRole } from '@routes/routesByRole';
import { RouteConfig } from '@routes/types';
import NoAuthorization from '@component/NoAuthorization/NoAuthorization';
import { decrypt } from '@src/utils';

const AuthGuard = ({
  children,
  routes,
}: {
  children: JSX.Element;
  routes: RouteConfig[];
}) => {
  const { userInfo, isContractorVersion } = useAppSelector(selectUsers);
  const navigate = useNavigate();
  const location = useLocation();

  if (shouldSkipAuth(location.pathname, location.search)) {
    return children;
  }

  if (!userInfo) {
    return <LoaderComponent dataTestId="authg-loader" />;
  }

  const matches = matchRoutes(
    routes as unknown as RouteObject[],
    location.pathname
  );
  const encryptedId = matches?.[matches.length - 1]?.params?.id;
  const decryptParams = encryptedId ? decrypt(encryptedId) : null;

  const isTheSamePayerCheck = (): boolean => {
    if (!decryptParams) {
      return true;
    }
    return userInfo?.associated === decryptParams?.payor;
  };

  const role = resolveRole({ userInfo, isContractorVersion });

  if (!role || !routesByRole[role] || !isTheSamePayerCheck()) {
    navigate('/no-authorization');
    return <NoAuthorization />;
  }

  return children;
};

export default AuthGuard;
